import React, { useState, useEffect } from "react";
import { User } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";
import {
  PROFESSIONS,
  INTERESTS,
  Profession,
  Interest,
} from "../../constants/userPreferences";
import "./Profile.css";

interface ProfileFormProps {
  user: User;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
}

// Define props for States
interface StateOption {
  stateprovinceabbrv: string;
  stateprovincename: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  user,
  onSubmit,
  onCancel,
}) => {
  const { getStateProvince } = useAPI();

  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [states, setStates] = useState<StateOption[]>([]);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    streetaddress: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    companyName: "",
    companyType: "",
    title: "",
    profession: [] as Profession[],
    professionOther: "",
    interests: [] as Interest[],
    interestsOther: "",
    activeind: true,
    entityid: "",
  });

  useEffect(() => {
    setFormData({
      firstName: user.firstName || "",
      middleName: user.middleName || "",
      lastName: user.lastName || "",
      streetaddress: user.streetaddress || "",
      city: user.city || "",
      state: user.state || "",
      zip: user.zip || "",
      country: user.country || "",
      phone: user.phone || "",
      companyName: user.companyName || "",
      companyType: user.companyType || "",
      title: user.title || "",
      profession: (user.profession || []) as Profession[],
      professionOther: user.professionOther || "",
      interests: (user.interests || []) as Interest[],
      interestsOther: user.interestsOther || "",
      activeind: user.activeind || true,
      entityid: user.entityid || "",
    });
  }, [user]);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateProvince();
        setStates(response);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    fetchStates();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (
    type: "profession" | "interests",
    value: Profession | Interest
  ) => {
    setFormData((prev) => {
      if (type === "profession") {
        const currentArray = prev.profession;
        const newArray = currentArray.includes(value as Profession)
          ? currentArray.filter((item: Profession) => item !== value)
          : [...currentArray, value as Profession];

        return {
          ...prev,
          profession: newArray,
        };
      } else {
        const currentArray = prev.interests;
        const newArray = currentArray.includes(value as Interest)
          ? currentArray.filter((item: Interest) => item !== value)
          : [...currentArray, value as Interest];

        return {
          ...prev,
          interests: newArray,
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const updatedUserData = {
        ...formData,
      };

      await onSubmit(updatedUserData);
      setSuccess("Profile updated successfully!");

      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while updating profile"
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="profile-form">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-group">
        <label>First Name</label>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Middle Name</label>
        <input
          type="text"
          name="middleName"
          value={formData.middleName}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Last Name</label>
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Address</label>
        <input
          type="text"
          name="streetaddress"
          value={formData.streetaddress}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>City</label>
        <input
          type="text"
          name="city"
          value={formData.city}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>State</label>
        <select
          id="state"
          name="state"
          value={formData.state}
          onChange={handleChange}
        >
          <option value="">-- Select State --</option>
          {states.map((s) => (
            <option key={s.stateprovinceabbrv} value={s.stateprovinceabbrv}>
              {s.stateprovincename}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Zip</label>
        <input
          type="text"
          name="zip"
          value={formData.zip}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Country</label>
        <input
          type="text"
          name="country"
          value={formData.country}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Phone</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Company Name</label>
        <input
          type="text"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Company Type</label>
        <input
          type="text"
          name="companyType"
          value={formData.companyType}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
        />
      </div>

      <div className="form-group">
        <label>Professions</label>
        <div className="checkbox-group">
          {PROFESSIONS.map((prof) => (
            <div key={prof} className="checkbox-item">
              <input
                type="checkbox"
                id={`prof-${prof}`}
                checked={formData.profession.includes(prof)}
                onChange={() => handleCheckboxChange("profession", prof)}
              />
              <label htmlFor={`prof-${prof}`}> {prof}</label>
            </div>
          ))}
        </div>
        {formData.profession.includes("Other") && (
          <input
            type="text"
            name="professionOther"
            value={formData.professionOther}
            onChange={handleChange}
            placeholder="Please specify other profession"
            className="other-input"
          />
        )}
      </div>

      <div className="form-group">
        <label>Interests</label>
        <div className="checkbox-group">
          {INTERESTS.map((interest) => (
            <div key={interest} className="checkbox-item">
              <input
                type="checkbox"
                id={`interest-${interest}`}
                checked={formData.interests.includes(interest)}
                onChange={() => handleCheckboxChange("interests", interest)}
              />
              <label htmlFor={`interest-${interest}`}> {interest}</label>
            </div>
          ))}
        </div>
        {formData.interests.includes("Other") && (
          <input
            type="text"
            name="interestsOther"
            value={formData.interestsOther}
            onChange={handleChange}
            placeholder="Please specify other interest"
            className="other-input"
          />
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-button">
          Cancel
        </button>
        <button type="submit" className="save-button">
          Save Changes
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;
